import React, { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { IoClose } from 'react-icons/io5';
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const AQ_PRICING_BASE_URL = process.env.REACT_APP_AQ_PRICING_BASE_URL;

const ROOMS_API_URL = `${BQ_BASE_URL}/bq/api/rooms/`;
const AVAILABILITY_API_URL = `${BQ_BASE_URL}/bq/api/availability`;
const FACTORS_API_URL = `${AQ_PRICING_BASE_URL}/pricing/api/get-factors`;
const ADMIN_ROOMTYPES_API_URL = `${BQ_BASE_URL}/bq/api/admin-roomtypes/`;
const ADD_ROOM_API_URL = `${BQ_BASE_URL}/bq/api/add-room/`;
const UPDATE_ROOM_API_URL = `${BQ_BASE_URL}/bq/api/update-room-status/`;

// ============================================
// CONDITIONS — codes used by backend
// ============================================
const CONDITIONS = [
  { code: 'all_working', label: 'All Working' },
  { code: 'ac_not_working', label: 'AC Not Working' },
  { code: 'geyser_not_working', label: 'Geyser Not Working' },
  { code: 'tv_not_working', label: 'TV Not Working' },
  { code: 'balcony_blocked', label: 'Balcony Blocked' },
  { code: 'noise_issue', label: 'Noise Issue' },
  { code: 'under_maintenance', label: 'Under Maintenance' },
];

const normalizeCondition = (raw) => {
  if (!raw) return 'all_working';
  const lower = String(raw).toLowerCase().trim().replace(/\s+/g, '_');
  const match = CONDITIONS.find((c) => c.code === lower);
  return match ? match.code : 'all_working';
};

// ============================================
// Default new-room state
// ============================================
const DEFAULT_NEW_ROOM = {
  room_no: '',
  room_type: '', // roomtypename (for display) — mapped → roomtypeid on submit
  status: 'Available',
  auto_assign_room: false,
  amenities: [], // string factorids
  condition: 'all_working',
  condition_notes: '',
};

const RoomManagement = () => {
  const [rooms, setRooms] = useState([]);
  const [roomData, setRoomData] = useState({});
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
  const navigate = useNavigate();

  const [isPopOpen, setIsPopOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [updatedRoom, setUpdatedRoom] = useState(selectedRoom);
  const [roomTypes, setRoomTypes] = useState([]);

  // Factors (amenity master list)
  const [roomAmenities, setRoomAmenities] = useState([]);
  const [allFactors, setAllFactors] = useState([]);
  const [factorsLoading, setFactorsLoading] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roomStatus, setRoomStatus] = useState('all');
  const [selectedRoomType, setSelectedRoomType] = useState('all');
  const [onlyAvailableRooms, setOnlyAvailableRooms] = useState(false);
  const [conditionFilter, setConditionFilter] = useState('all');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newRoom, setNewRoom] = useState({ ...DEFAULT_NEW_ROOM });

  // ============================================
  // Fetch room types (from admin-roomtypes)
  // ============================================
  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const response = await fetch(ADMIN_ROOMTYPES_API_URL, {
          headers: { accept: '*/*' },
        });
        const data = await response.json();

        // API returns { roomtypes: [...], all_amenities: [...], all_roomservices: [...] }
        const list = Array.isArray(data) ? data : data?.roomtypes || [];

        // Keep only what we need
        const cleaned = list.map((rt) => ({
          roomtypeid: rt.roomtypeid,
          roomtypename: rt.roomtypename,
          baseprice: rt.baseprice,
          description: rt.description,
        }));

        console.log('Fetched room types:', cleaned);
        setRoomTypes(cleaned);
      } catch (error) {
        console.error('Error fetching room types:', error);
        toast.error('Could not load room types');
        setRoomTypes([]);
      }
    };

    fetchRoomTypes();
  }, []);

  // ============================================
  // Fetch amenity factors
  // ============================================
  const fetchRoomAmenityFactors = async () => {
    setFactorsLoading(true);
    try {
      const response = await fetch(FACTORS_API_URL, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to fetch factors');
      const data = await response.json();

      setAllFactors(data || []);

      const amenities = (data || [])
        .filter((f) => f.is_room_amenity === true && f.isactive === true)
        .map((f) => ({
          factorid: f.factorid,
          code: String(f.factorid),
          label: f.factorname,
          value: f.default_value ?? f.defaultpercentage ?? 0,
          value_type: f.value_type || 'flat',
        }));

      setRoomAmenities(amenities);
    } catch (error) {
      console.error('Error fetching factors:', error);
      toast.error('Could not load room amenities');
      setRoomAmenities([]);
    } finally {
      setFactorsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoomAmenityFactors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // Helpers
  // ============================================
  const getConditionLabel = (code) => {
    const c = CONDITIONS.find((x) => x.code === code);
    return c ? c.label : 'All Working';
  };

  const getConditionColor = (code) => {
    if (!code || code === 'all_working') return 'text-green-700';
    if (code === 'under_maintenance') return 'text-red-700';
    return 'text-orange-700';
  };

  const formatAmenityValue = (value, value_type) => {
    if (value_type === 'percentage') return `${value}%`;
    return `₹${value}`;
  };

  // Resolve roomtypeid from selected roomtypename
  const resolveRoomTypeId = (name) => {
    if (!name || !Array.isArray(roomTypes) || roomTypes.length === 0) return null;
    const target = String(name).trim().toLowerCase();
    const found = roomTypes.find(
      (r) =>
        String(r.roomtypename || '')
          .trim()
          .toLowerCase() === target
    );
    return found?.roomtypeid ?? null;
  };

  // ============================================
  // ADD ROOM — POST /bq/api/add-room/
  // ============================================
  const handleAddRoom = async () => {
    // ---- Validation ----
    if (!newRoom.room_no || newRoom.room_no === '') {
      toast.error('Room number is required');
      return;
    }
    if (!newRoom.room_type) {
      toast.error('Room type is required');
      return;
    }

    const roomtypeid = resolveRoomTypeId(newRoom.room_type);
    if (!roomtypeid) {
      console.error('Room type lookup failed', {
        selected: newRoom.room_type,
        available: roomTypes,
      });
      toast.error(
        roomTypes.length === 0
          ? 'Room types not loaded. Check /bq/api/admin-roomtypes/'
          : `Room type "${newRoom.room_type}" not found in list.`
      );
      return;
    }

    const roomnumber = parseInt(newRoom.room_no, 10);
    if (isNaN(roomnumber)) {
      toast.error('Room number must be a valid number');
      return;
    }

    const payload = {
      roomnumber,
      roomtypeid,
      status: newRoom.status,
      auto_assign_room: newRoom.auto_assign_room || false,
      amenities: (newRoom.amenities || []).map((a) => String(a)),
      condition: newRoom.condition || 'all_working',
      condition_notes: newRoom.condition_notes || '',
    };

    console.log('POST /bq/api/add-room/ payload:', payload);

    try {
      const response = await fetch(ADD_ROOM_API_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (response.ok) {
        toast.success(responseData.message || 'Room added successfully!');
        setIsModalOpen(false);
        setNewRoom({ ...DEFAULT_NEW_ROOM });
        await fetchRoomDetails();
      } else {
        toast.error(responseData.detail || 'Failed to add room.');
      }
    } catch (error) {
      console.error('Error adding room:', error);
      toast.error('Error adding room: ' + error.message);
    }
  };

  // ============================================
  // Update room
  // ============================================
  useEffect(() => {
    setUpdatedRoom(selectedRoom);
  }, [selectedRoom]);

  const handleUpdate = async () => {
    if (!selectedRoom?.roomid) {
      toast.error('Missing room ID');
      return;
    }

    if (!selectedRoom.roomtypeid) {
      toast.error('Room type is required');
      return;
    }

    const payload = {
      roomid: selectedRoom.roomid,
      status: selectedRoom.status,
      auto_assign_room: selectedRoom.auto_assign_room || false,
      roomtypeid: selectedRoom.roomtypeid,
      amenities: (selectedRoom.amenities || []).map((a) =>
        typeof a === 'object' ? String(a.factorid) : String(a)
      ),
      condition: selectedRoom.condition || 'all_working',
      condition_notes: selectedRoom.condition_notes || '',
    };

    console.log('PUT /bq/api/update-room-status/ payload:', payload);

    try {
      const response = await fetch(UPDATE_ROOM_API_URL, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();
      console.log('Update response:', responseData);

      if (response.ok) {
        if (responseData.warning) {
          toast.warning(responseData.warning);
        } else {
          toast.success(responseData.message || 'Room updated successfully.');
        }
        modalClose();
        await fetchRoomDetails();
      } else {
        toast.error('Failed to update room: ' + (responseData.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating room:', error);
      toast.error('Error updating room: ' + error.message);
    }
  };

  const handleDeleteRoom = async (roomnumber) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/rooms/${roomnumber}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });

      if (response.ok) {
        toast.success('Room deleted successfully');
        setIsPopOpen(false);
        setTimeout(() => window.location.reload(), 1000);
      } else {
        const result = await response.json();
        toast.error(result.detail || 'Failed to delete room');
      }
    } catch (error) {
      console.error('Error deleting room:', error);
      toast.error('Error deleting room');
    }
  };

  // ============================================
  // Fetch rooms
  // ============================================
  const fetchRoomDetails = async () => {
    try {
      const response = await fetch(ROOMS_API_URL, {
        headers: { accept: '*/*' },
      });
      const data = await response.json();

      const list = Array.isArray(data) ? data : data?.rooms || [];

      const normalized = list.map((r) => {
        const amenityObjects = (r.amenities || []).map((a) => {
          if (typeof a === 'object' && a !== null) {
            return {
              factorid: a.factorid,
              name: a.name,
              value: a.value,
              value_type: a.value_type,
              contribution: a.contribution,
            };
          }
          const found = allFactors.find((f) => String(f.factorid) === String(a));
          return {
            factorid: Number(a),
            name: found ? found.factorname : String(a),
            value: found ? (found.default_value ?? found.defaultpercentage ?? 0) : 0,
            value_type: found ? found.value_type || 'flat' : 'flat',
            contribution: 0,
          };
        });

        const amenityIds = amenityObjects.map((a) => String(a.factorid));

        return {
          roomid: r.roomid,
          roomnumber: r.roomnumber,
          roomtypeid: r.roomtypeid,
          status: r.status,
          auto_assign_room: r.auto_assign_room || false,
          amenities: amenityObjects,
          amenityIds,
          condition: normalizeCondition(r.condition),
          condition_notes: r.condition_notes || '',
          roomtype: r.roomtype?.roomtypename || '',
          room_type: r.roomtype?.roomtypename || '',
          baseprice: r.roomtype?.baseprice ?? 0,
          base_price: r.base_price ?? r.roomtype?.baseprice ?? 0,
          amenities_total: r.amenities_total ?? 0,
          flat_total: r.flat_total ?? 0,
          percentage_total: r.percentage_total ?? 0,
          final_price: r.final_price ?? r.roomtype?.baseprice ?? 0,
        };
      });

      setRooms(normalized);

      // Group: roomType → status (lowercase) → [rooms]
      const grouped = {};
      normalized.forEach((room) => {
        const rtype = room.roomtype || 'Unknown';
        const status = String(room.status || '').toLowerCase();

        if (!grouped[rtype]) grouped[rtype] = {};
        if (!grouped[rtype][status]) grouped[rtype][status] = [];
        grouped[rtype][status].push(room);
      });

      setRoomData(grouped);
    } catch (error) {
      console.error('Error fetching room details:', error);
      toast.error('Could not load rooms');
    }
  };

  useEffect(() => {
    fetchRoomDetails();

    const fetchAvailableRoomTypes = async () => {
      try {
        const response = await fetch(AVAILABILITY_API_URL);
        const data = await response.json();
        setAvailableRoomTypes(data.available_room_types);
      } catch (error) {
        console.error('Error fetching available room types:', error);
        setAvailableRoomTypes([]);
      }
    };

    fetchAvailableRoomTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // Filter
  // ============================================
  const filteredRooms = Object.keys(roomData).map((roomType) => {
    const roomsInType = Object.keys(roomData[roomType]).flatMap((status) =>
      roomData[roomType][status].filter((room) => {
        const matchesSearch =
          (room?.baseprice?.toString()?.includes(searchTerm) ?? false) ||
          (room?.roomnumber?.toString()?.includes(searchTerm) ?? false);

        const matchesStatus =
          roomStatus === 'all' || String(room.status).toLowerCase() === roomStatus;

        const matchesRoomType =
          selectedRoomType === 'all' ||
          String(room.room_type).toLowerCase() === selectedRoomType.toLowerCase();

        const matchesAvailability =
          !onlyAvailableRooms || String(room.status).toLowerCase() === 'available';

        const matchesCondition = conditionFilter === 'all' || room.condition === conditionFilter;

        return (
          matchesSearch &&
          matchesStatus &&
          matchesRoomType &&
          matchesAvailability &&
          matchesCondition
        );
      })
    );

    const availableCount = roomsInType.filter(
      (r) => String(r.status).toLowerCase() === 'available'
    ).length;
    const occupiedCount = roomsInType.filter(
      (r) => String(r.status).toLowerCase() === 'occupied'
    ).length;
    const reservedCount = roomsInType.filter(
      (r) => String(r.status).toLowerCase() === 'reserved'
    ).length;
    const maintenanceCount = roomsInType.filter(
      (r) => String(r.status).toLowerCase() === 'under maintenance'
    ).length;

    return {
      roomType,
      availableCount,
      occupiedCount,
      reservedCount,
      maintenanceCount,
      rooms: roomsInType,
    };
  });

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoomStatus('all');
    setSelectedRoomType('all');
    setOnlyAvailableRooms(false);
    setConditionFilter('all');
  };

  const handleOpenModal = () => {
    setNewRoom({ ...DEFAULT_NEW_ROOM });
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNewRoom({ ...DEFAULT_NEW_ROOM });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRoom((prev) => ({ ...prev, [name]: value }));
  };

  // Toggle amenity in NEW room modal
  const toggleNewRoomAmenity = (code) => {
    setNewRoom((prev) => {
      const current = prev.amenities || [];
      const updated = current.includes(code)
        ? current.filter((a) => a !== code)
        : [...current, code];
      return { ...prev, amenities: updated };
    });
  };

  const openModal = (room) => {
    setSelectedRoom({
      ...room,
      amenities: room.amenityIds || [],
      condition: room.condition || 'all_working',
      condition_notes: room.condition_notes || '',
      auto_assign_room: room.auto_assign_room || false,
      roomtypeid: room.roomtypeid,
      room_type: room.roomtype || room.room_type || '',
    });
    setIsPopOpen(true);
  };

  const modalClose = () => {
    setIsPopOpen(false);
    setSelectedRoom(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelectedRoom((prev) => ({ ...prev, [name]: value }));
  };

  const toggleAmenity = (code) => {
    setSelectedRoom((prev) => {
      const current = prev.amenities || [];
      const updated = current.includes(code)
        ? current.filter((a) => a !== code)
        : [...current, code];
      return { ...prev, amenities: updated };
    });
  };

  return (
    <div className="p-6 bg-gray-100 ">
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Room Inventory</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/room-management/overview')}
            className="btn"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/room-management/rooms')}
            className="btn text-black font-bold"
          >
            Room Management
          </button>
        </div>
      </header>

      <h1 className="text-2xl font-bold text-center text-black">Room Management</h1>
      <h1 className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
        Efficient room management ensures seamless booking, maintenance, and comfort, providing
        guests with an exceptional experience throughout their stay.
      </h1>
      <hr className="border border-gray-400 w-1/2 mx-auto mb-7" />

      {/* Filters */}
      <div className="flex justify-between items-center gap-2 mb-8 text-sm w-full flex-wrap">
        <div className="w-full mb-4 sm:mb-0 relative flex-1 min-w-[180px]">
          <input
            type="text"
            className="w-full px-2 py-2 pl-10 border border-gray-300 rounded-md"
            placeholder="Find a room"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FiSearch
            size={16}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600"
          />
        </div>

        <div className="w-full mb-4 sm:mb-0 flex-1 min-w-[150px]">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            value={roomStatus}
            onChange={(e) => setRoomStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="occupied">Occupied</option>
            <option value="under maintenance">Under Maintenance</option>
          </select>
        </div>

        <div className="w-full mb-4 sm:mb-0 flex-1 min-w-[150px]">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none"
            value={selectedRoomType}
            onChange={(e) => setSelectedRoomType(e.target.value)}
          >
            <option value="all">All Room Types</option>
            {Object.keys(roomData).map((rt) => (
              <option key={rt} value={rt}>
                {rt}
              </option>
            ))}
          </select>
        </div>

        <div className="w-full mb-4 sm:mb-0 flex-1 min-w-[150px]">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none"
            value={conditionFilter}
            onChange={(e) => setConditionFilter(e.target.value)}
          >
            <option value="all">All Conditions</option>
            {CONDITIONS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <button
            onClick={handleOpenModal}
            className="px-6 py-2 bg-black text-white rounded-lg whitespace-nowrap"
          >
            Add Room
          </button>
        </div>

        <Link to={'/dashboard/mdm/room-management/rooms/addroomtype'}>
          <div>
            <button className="px-6 py-2 bg-black text-white rounded-lg whitespace-nowrap">
              Add Room Type
            </button>
          </div>
        </Link>

        <div>
          <button
            onClick={handleClearFilters}
            className="px-6 py-2 bg-red-500 text-white rounded-lg whitespace-nowrap"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="w-full mb-4">
        {filteredRooms.length > 0 ? (
          filteredRooms
            .filter(({ roomType }) => selectedRoomType === roomType)
            .map(
              ({
                roomType,
                availableCount,
                occupiedCount,
                reservedCount,
                maintenanceCount,
                rooms,
              }) => (
                <div key={roomType} className="mb-6">
                  <h2 className="text-lg font-bold mb-2 text-gray-800">{roomType}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Total Rooms</h3>
                      <p className="text-3xl font-bold text-gray-800">{rooms.length}</p>
                    </div>
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Available Rooms</h3>
                      <p className="text-3xl font-bold text-gray-800">{availableCount}</p>
                    </div>
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Reserved Rooms</h3>
                      <p className="text-3xl font-bold text-gray-800">{reservedCount}</p>
                    </div>
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Occupied Rooms</h3>
                      <p className="text-3xl font-bold text-gray-800">{occupiedCount}</p>
                    </div>
                    <div className="bg-white p-5 rounded-lg shadow-md">
                      <h3 className="font-semibold text-lg text-gray-700">Under Maintenance</h3>
                      <p className="text-3xl font-bold text-gray-800">{maintenanceCount}</p>
                    </div>
                  </div>
                </div>
              )
            )
        ) : (
          <div className="text-center text-gray-700 mt-4 p-4">
            <p className="text-lg font-semibold text-gray-800">Loading...</p>
          </div>
        )}
      </div>

      {/* Room Tables */}
      <div className="space-y-8 text-sm">
        {filteredRooms.map(({ roomType, rooms }) => {
          if (rooms.length === 0) return null;

          return (
            <div key={roomType}>
              <h2 className="bg-white text-black font-bold text-left p-3 text-base rounded-t-lg shadow-md">
                {roomType}
              </h2>

              <div className="overflow-x-auto rounded-b-lg">
                <table className="min-w-full table-auto bg-white">
                  <thead className="bg-gray-900 text-white">
                    <tr>
                      <th className="py-3 px-4 text-left font-medium">Room No.</th>
                      <th className="py-3 px-4 text-left font-medium">Status</th>
                      <th className="py-3 px-4 text-left font-medium">Base Price</th>
                      <th className="py-3 px-4 text-left font-medium">Amenities</th>
                      <th className="py-3 px-4 text-left font-medium">Amenities Total</th>
                      <th className="py-3 px-4 text-left font-medium">Final Price</th>
                      <th className="py-3 px-4 text-left font-medium">Condition</th>
                      <th className="py-3 px-4 text-left font-medium">Auto-Assign</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 border border-gray-200">
                    {rooms.map((room, index) => {
                      const amenities = room.amenities || [];
                      const condition = room.condition || 'all_working';

                      return (
                        <tr
                          key={room.roomid}
                          className={`${
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-100'
                          } hover:bg-gray-200 cursor-pointer align-top`}
                          onClick={() => openModal(room)}
                        >
                          <td className="py-3 px-4 font-semibold">{room.roomnumber}</td>
                          <td className="py-3 px-4">{room.status}</td>
                          <td className="py-3 px-4">₹{room.base_price}</td>

                          <td className="py-3 px-4">
                            {amenities.length > 0 ? (
                              <div className="flex flex-col gap-1">
                                {amenities.map((a, i) => (
                                  <div
                                    key={`${a.factorid}-${i}`}
                                    className="flex items-center gap-2 text-xs"
                                  >
                                    <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                      {a.name}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">None</span>
                            )}
                          </td>

                          <td className="py-3 px-4 font-medium">
                            {room.amenities_total > 0 ? (
                              <span className="text-green-700">+₹{room.amenities_total}</span>
                            ) : (
                              <span className="text-gray-400">₹0</span>
                            )}
                          </td>

                          <td className="py-3 px-4 font-bold text-black">₹{room.final_price}</td>

                          <td className="py-3 px-4">
                            <span className={`font-semibold ${getConditionColor(condition)}`}>
                              {getConditionLabel(condition)}
                            </span>
                            {condition !== 'all_working' && room.condition_notes && (
                              <p className="text-xs text-gray-500 mt-0.5">{room.condition_notes}</p>
                            )}
                          </td>

                          <td className="py-3 px-4">
                            <label className="relative inline-flex items-center cursor-default pointer-events-none">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={room.auto_assign_room}
                                readOnly
                                disabled
                              />
                              <div className="w-10 h-6 bg-gray-300 rounded-full peer peer-checked:bg-black transition-all"></div>
                              <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-full"></div>
                            </label>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {/* ============================================ */}
      {/* Add Room Modal */}
      {/* ============================================ */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center p-4">
          <div className="relative bg-white p-6 rounded-md shadow-lg w-full max-w-2xl text-xs max-h-[90vh] overflow-y-auto">
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-3 text-red-500 hover:text-red-600"
            >
              <IoClose size={28} />
            </button>

            <h2 className="text-xl font-bold mb-4">Add New Room</h2>

            <div className="space-y-3">
              {/* Room Number + Room Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold mb-1">Room Number *</label>
                  <input
                    type="number"
                    name="room_no"
                    placeholder="e.g. 501"
                    value={newRoom.room_no}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Room Type *</label>
                  <select
                    name="room_type"
                    value={newRoom.room_type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="" disabled>
                      Select Room Type
                    </option>
                    {roomTypes.length === 0 ? (
                      <option disabled value="">
                        Loading room types...
                      </option>
                    ) : (
                      roomTypes.map((type) => (
                        <option key={type.roomtypeid} value={type.roomtypename}>
                          {type.roomtypename}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              {/* Status + Auto-Assign */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold mb-1">Status *</label>
                  <select
                    name="status"
                    value={newRoom.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="Available">Available</option>
                    <option value="Reserved">Reserved</option>
                    <option value="Occupied">Occupied</option>
                    <option value="Under Maintenance">Under Maintenance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Auto-Assign Room</label>
                  <button
                    type="button"
                    onClick={() =>
                      setNewRoom((prev) => ({
                        ...prev,
                        auto_assign_room: !prev.auto_assign_room,
                      }))
                    }
                    className={`mt-1 w-11 h-6 rounded-full transition ${
                      newRoom.auto_assign_room ? 'bg-black' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`block w-4 h-4 bg-white rounded-full transition-transform  ${
                        newRoom.auto_assign_room ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="ml-3 text-xs text-gray-500">
                    {newRoom.auto_assign_room ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              {/* Amenities */}
              <div className="border border-gray-200 rounded-md p-3">
                <label className="block text-sm font-semibold mb-1">Room Attributes</label>
                <p className="text-xs text-gray-500 mb-2">
                  Tick the attributes this room has. Prices will auto-apply to the room's final
                  rate.
                </p>

                {factorsLoading ? (
                  <p className="text-xs text-gray-400 italic">Loading attributes...</p>
                ) : roomAmenities.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">
                    No room attributes configured. Create factors marked as "Room Attribute" in
                    Factor Master first.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {roomAmenities.map((amenity) => {
                      const isChecked = (newRoom.amenities || []).includes(amenity.code);
                      return (
                        <label
                          key={amenity.code}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md border-2 cursor-pointer transition ${
                            isChecked
                              ? 'bg-blue-50 border-blue-400 text-blue-800 font-semibold'
                              : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleNewRoomAmenity(amenity.code)}
                            className="h-4 w-4"
                          />
                          <span className="text-xs flex-1">{amenity.label}</span>
                          <span className="text-[10px] text-gray-500">
                            {amenity.value_type === 'percentage'
                              ? `${amenity.value}%`
                              : `₹${amenity.value}`}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Condition + Notes */}
              <div className="border border-gray-200 rounded-md p-3">
                <label className="block text-sm font-semibold mb-1">Condition</label>
                <select
                  name="condition"
                  value={newRoom.condition}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
                >
                  {CONDITIONS.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>

                {newRoom.condition && newRoom.condition !== 'all_working' && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium mb-1">
                      Condition Notes (Optional)
                    </label>
                    <input
                      type="text"
                      name="condition_notes"
                      value={newRoom.condition_notes || ''}
                      onChange={handleInputChange}
                      placeholder="e.g., Compressor failure, technician arriving tomorrow"
                      className="w-full px-3 py-2 border rounded-md text-xs"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 text-sm mt-5">
              <button
                onClick={handleCloseModal}
                className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddRoom}
                className="px-5 py-2 bg-black text-white rounded-md hover:bg-gray-800"
              >
                Add Room
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Room Detail / Edit Modal */}
      {isPopOpen && selectedRoom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="relative bg-white p-8 rounded-lg shadow-lg w-full max-w-2xl text-sm text-black max-h-[90vh] overflow-y-auto">
            <button
              onClick={modalClose}
              className="absolute top-4 right-3 text-red-500 hover:text-red-600"
            >
              <IoClose size={28} />
            </button>
            <h3 className="text-xl font-bold text-center">Room Details</h3>
            <p className="text-sm text-gray-500 mb-4 text-center font-semibold">
              Update the room details below
            </p>
            <div className="space-y-4 text-xs">
              <div>
                <label className="block text-black">Room Number</label>
                <input
                  type="text"
                  value={selectedRoom.roomnumber}
                  disabled
                  className="w-full px-4 py-2 border rounded-md cursor-not-allowed"
                />
              </div>

              {/* Room Type — now editable */}
              <div>
                <label className="block text-black">Room Type</label>
                <select
                  name="roomtypeid"
                  value={selectedRoom.roomtypeid ?? ''}
                  onChange={(e) => {
                    const newId = Number(e.target.value);
                    const matched = roomTypes.find((rt) => rt.roomtypeid === newId);
                    setSelectedRoom((prev) => ({
                      ...prev,
                      roomtypeid: newId,
                      room_type: matched?.roomtypename || prev.room_type,
                      roomtype: matched?.roomtypename || prev.roomtype,
                      baseprice: matched?.baseprice ?? prev.baseprice,
                      base_price: matched?.baseprice ?? prev.base_price,
                    }));
                  }}
                  className="w-full px-4 py-2 border rounded-md"
                >
                  <option value="" disabled>
                    Select Room Type
                  </option>
                  {roomTypes.length === 0 ? (
                    <option disabled value="">
                      Loading room types...
                    </option>
                  ) : (
                    roomTypes.map((type) => (
                      <option key={type.roomtypeid} value={type.roomtypeid}>
                        {type.roomtypename}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label className="block text-black">Status</label>
                <select
                  name="status"
                  value={selectedRoom.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-md"
                >
                  <option value="Available">Available</option>
                  <option value="Reserved">Reserved</option>
                  <option value="Occupied">Occupied</option>
                  <option value="Under Maintenance">Under Maintenance</option>
                </select>
              </div>

              <div>
                <label className="block text-black">Base Price</label>
                <input
                  type="number"
                  value={selectedRoom.base_price}
                  disabled
                  className="w-full px-4 py-2 border rounded-md cursor-not-allowed"
                />
              </div>

              {/* Amenities */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-black text-sm font-semibold mb-2">
                  Room Amenities
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Tick which amenities this room actually has. Pricing updates automatically.
                </p>

                {factorsLoading ? (
                  <p className="text-xs text-gray-400 italic">Loading amenities...</p>
                ) : roomAmenities.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">
                    No room amenities configured. Create factors marked as "Room Amenity" in Factor
                    Master.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {roomAmenities.map((amenity) => {
                      const isChecked = (selectedRoom.amenities || []).includes(amenity.code);
                      return (
                        <label
                          key={amenity.code}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md border-2 cursor-pointer transition ${
                            isChecked
                              ? 'bg-blue-50 border-blue-400 text-blue-800 font-semibold'
                              : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleAmenity(amenity.code)}
                            className="h-4 w-4"
                          />
                          <span className="text-xs flex-1">{amenity.label}</span>
                          <span className="text-[10px] text-gray-500">
                            {amenity.value_type === 'percentage'
                              ? `${amenity.value}%`
                              : `₹${amenity.value}`}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Condition */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-black text-sm font-semibold mb-2">
                  Current Condition
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Mark any issue with this room. This affects how it's handled during bookings.
                </p>

                <select
                  name="condition"
                  value={selectedRoom.condition || 'all_working'}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-md"
                >
                  {CONDITIONS.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>

                {selectedRoom.condition && selectedRoom.condition !== 'all_working' && (
                  <div className="mt-3">
                    <label className="block text-black text-xs font-medium mb-1">
                      Condition Notes (Optional)
                    </label>
                    <input
                      type="text"
                      name="condition_notes"
                      value={selectedRoom.condition_notes || ''}
                      onChange={handleChange}
                      placeholder="e.g., Compressor failure, technician arriving tomorrow"
                      className="w-full px-3 py-2 border rounded-md text-xs"
                    />
                  </div>
                )}
              </div>

              {/* Auto-Assign */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <label className="block text-black text-sm font-semibold">
                      Auto-Assign Room
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      When enabled, this room is prioritized for automatic guest assignment.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={selectedRoom.auto_assign_room}
                      onChange={(e) =>
                        setSelectedRoom((prev) => ({
                          ...prev,
                          auto_assign_room: e.target.checked,
                        }))
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-black transition-all"></div>
                    <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-full"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={handleUpdate} className="bg-black text-white px-4 py-2 rounded-md">
                Save Changes
              </button>

              <button
                onClick={() => handleDeleteRoom(selectedRoom.roomnumber)}
                className="px-5 py-2 bg-red-500 text-white rounded-md"
              >
                Delete Room
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar className="mt-12" />
    </div>
  );
};

export default RoomManagement;

// import React, { useEffect, useState } from 'react';
// import { FiSearch } from 'react-icons/fi';
// import { Link, useNavigate } from 'react-router-dom';
// import { ToastContainer, toast } from 'react-toastify';
// import { IoClose } from 'react-icons/io5';
// import 'react-toastify/dist/ReactToastify.css';
// import 'react-datepicker/dist/react-datepicker.css';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
// const AQ_PRICING_BASE_URL = process.env.REACT_APP_AQ_PRICING_BASE_URL;

// const ROOMS_API_URL = `${BQ_BASE_URL}/bq/api/rooms/`;
// const AVAILABILITY_API_URL = `${BQ_BASE_URL}/bq/api/availability`;
// const FACTORS_API_URL = `${AQ_PRICING_BASE_URL}/pricing/api/get-factors`;
// const ADMIN_ROOMTYPES_API_URL = `${BQ_BASE_URL}/bq/api/admin-roomtypes/`;
// const ADD_ROOM_API_URL = `${BQ_BASE_URL}/bq/api/add-room/`;
// const UPDATE_ROOM_API_URL = `${BQ_BASE_URL}/bq/api/update-room-status/`;

// // ============================================
// // CONDITIONS — codes used by backend
// // ============================================
// const CONDITIONS = [
//   { code: 'all_working', label: 'All Working' },
//   { code: 'ac_not_working', label: 'AC Not Working' },
//   { code: 'geyser_not_working', label: 'Geyser Not Working' },
//   { code: 'tv_not_working', label: 'TV Not Working' },
//   { code: 'balcony_blocked', label: 'Balcony Blocked' },
//   { code: 'noise_issue', label: 'Noise Issue' },
//   { code: 'under_maintenance', label: 'Under Maintenance' },
// ];

// const normalizeCondition = (raw) => {
//   if (!raw) return 'all_working';
//   const lower = String(raw).toLowerCase().trim().replace(/\s+/g, '_');
//   const match = CONDITIONS.find((c) => c.code === lower);
//   return match ? match.code : 'all_working';
// };

// // ============================================
// // Default new-room state
// // ============================================
// const DEFAULT_NEW_ROOM = {
//   room_no: '',
//   room_type: '', // roomtypename (for display) — mapped → roomtypeid on submit
//   status: 'Available',
//   auto_assign_room: false,
//   amenities: [], // string factorids
//   condition: 'all_working',
//   condition_notes: '',
// };

// const RoomManagement = () => {
//   const [rooms, setRooms] = useState([]);
//   const [roomData, setRoomData] = useState({});
//   const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
//   const navigate = useNavigate();

//   const [isPopOpen, setIsPopOpen] = useState(false);
//   const [selectedRoom, setSelectedRoom] = useState(null);
//   const [updatedRoom, setUpdatedRoom] = useState(selectedRoom);
//   const [roomTypes, setRoomTypes] = useState([]);

//   // Factors (amenity master list)
//   const [roomAmenities, setRoomAmenities] = useState([]);
//   const [allFactors, setAllFactors] = useState([]);
//   const [factorsLoading, setFactorsLoading] = useState(false);

//   // Filter states
//   const [searchTerm, setSearchTerm] = useState('');
//   const [roomStatus, setRoomStatus] = useState('all');
//   const [selectedRoomType, setSelectedRoomType] = useState('all');
//   const [onlyAvailableRooms, setOnlyAvailableRooms] = useState(false);
//   const [conditionFilter, setConditionFilter] = useState('all');

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [newRoom, setNewRoom] = useState({ ...DEFAULT_NEW_ROOM });

//   // ============================================
//   // Fetch room types (from admin-roomtypes)
//   // ============================================
//   useEffect(() => {
//     const fetchRoomTypes = async () => {
//       try {
//         const response = await fetch(ADMIN_ROOMTYPES_API_URL, {
//           headers: { accept: '*/*' },
//         });
//         const data = await response.json();

//         // API returns { roomtypes: [...], all_amenities: [...], all_roomservices: [...] }
//         const list = Array.isArray(data) ? data : data?.roomtypes || [];

//         // Keep only what we need
//         const cleaned = list.map((rt) => ({
//           roomtypeid: rt.roomtypeid,
//           roomtypename: rt.roomtypename,
//           baseprice: rt.baseprice,
//           description: rt.description,
//         }));

//         console.log('Fetched room types:', cleaned);
//         setRoomTypes(cleaned);
//       } catch (error) {
//         console.error('Error fetching room types:', error);
//         toast.error('Could not load room types');
//         setRoomTypes([]);
//       }
//     };

//     fetchRoomTypes();
//   }, []);

//   // ============================================
//   // Fetch amenity factors
//   // ============================================
//   const fetchRoomAmenityFactors = async () => {
//     setFactorsLoading(true);
//     try {
//       const response = await fetch(FACTORS_API_URL, {
//         method: 'GET',
//         headers: { accept: 'application/json' },
//       });
//       if (!response.ok) throw new Error('Failed to fetch factors');
//       const data = await response.json();

//       setAllFactors(data || []);

//       const amenities = (data || [])
//         .filter((f) => f.is_room_amenity === true && f.isactive === true)
//         .map((f) => ({
//           factorid: f.factorid,
//           code: String(f.factorid),
//           label: f.factorname,
//           value: f.default_value ?? f.defaultpercentage ?? 0,
//           value_type: f.value_type || 'flat',
//         }));

//       setRoomAmenities(amenities);
//     } catch (error) {
//       console.error('Error fetching factors:', error);
//       toast.error('Could not load room amenities');
//       setRoomAmenities([]);
//     } finally {
//       setFactorsLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchRoomAmenityFactors();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   // ============================================
//   // Helpers
//   // ============================================
//   const getConditionLabel = (code) => {
//     const c = CONDITIONS.find((x) => x.code === code);
//     return c ? c.label : 'All Working';
//   };

//   const getConditionColor = (code) => {
//     if (!code || code === 'all_working') return 'text-green-700';
//     if (code === 'under_maintenance') return 'text-red-700';
//     return 'text-orange-700';
//   };

//   const formatAmenityValue = (value, value_type) => {
//     if (value_type === 'percentage') return `${value}%`;
//     return `₹${value}`;
//   };

//   // Resolve roomtypeid from selected roomtypename
//   const resolveRoomTypeId = (name) => {
//     if (!name || !Array.isArray(roomTypes) || roomTypes.length === 0) return null;
//     const target = String(name).trim().toLowerCase();
//     const found = roomTypes.find(
//       (r) =>
//         String(r.roomtypename || '')
//           .trim()
//           .toLowerCase() === target
//     );
//     return found?.roomtypeid ?? null;
//   };

//   // ============================================
//   // ADD ROOM — POST /bq/api/add-room/
//   // ============================================
//   const handleAddRoom = async () => {
//     // ---- Validation ----
//     if (!newRoom.room_no || newRoom.room_no === '') {
//       toast.error('Room number is required');
//       return;
//     }
//     if (!newRoom.room_type) {
//       toast.error('Room type is required');
//       return;
//     }

//     const roomtypeid = resolveRoomTypeId(newRoom.room_type);
//     if (!roomtypeid) {
//       console.error('Room type lookup failed', {
//         selected: newRoom.room_type,
//         available: roomTypes,
//       });
//       toast.error(
//         roomTypes.length === 0
//           ? 'Room types not loaded. Check /bq/api/admin-roomtypes/'
//           : `Room type "${newRoom.room_type}" not found in list.`
//       );
//       return;
//     }

//     const roomnumber = parseInt(newRoom.room_no, 10);
//     if (isNaN(roomnumber)) {
//       toast.error('Room number must be a valid number');
//       return;
//     }

//     const payload = {
//       roomnumber,
//       roomtypeid,
//       status: newRoom.status,
//       auto_assign_room: newRoom.auto_assign_room || false,
//       amenities: (newRoom.amenities || []).map((a) => String(a)),
//       condition: newRoom.condition || 'all_working',
//       condition_notes: newRoom.condition_notes || '',
//     };

//     console.log('POST /bq/api/add-room/ payload:', payload);

//     try {
//       const response = await fetch(ADD_ROOM_API_URL, {
//         method: 'POST',
//         headers: {
//           Accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(payload),
//       });

//       const responseData = await response.json();

//       if (response.ok) {
//         toast.success(responseData.message || 'Room added successfully!');
//         setIsModalOpen(false);
//         setNewRoom({ ...DEFAULT_NEW_ROOM });
//         await fetchRoomDetails();
//       } else {
//         toast.error(responseData.detail || 'Failed to add room.');
//       }
//     } catch (error) {
//       console.error('Error adding room:', error);
//       toast.error('Error adding room: ' + error.message);
//     }
//   };

//   // ============================================
//   // Update room
//   // ============================================
//   useEffect(() => {
//     setUpdatedRoom(selectedRoom);
//   }, [selectedRoom]);

//   const handleUpdate = async () => {
//     if (!selectedRoom?.roomid) {
//       toast.error('Missing room ID');
//       return;
//     }

//     const payload = {
//       roomid: selectedRoom.roomid,
//       status: selectedRoom.status,
//       auto_assign_room: selectedRoom.auto_assign_room || false,
//       amenities: (selectedRoom.amenities || []).map((a) =>
//         typeof a === 'object' ? String(a.factorid) : String(a)
//       ),
//       condition: selectedRoom.condition || 'all_working',
//       condition_notes: selectedRoom.condition_notes || '',
//     };

//     console.log('PUT /bq/api/update-room-status/ payload:', payload);

//     try {
//       const response = await fetch(UPDATE_ROOM_API_URL, {
//         method: 'PUT',
//         headers: {
//           Accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(payload),
//       });

//       const responseData = await response.json();
//       console.log('Update response:', responseData);

//       if (response.ok) {
//         if (responseData.warning) {
//           toast.warning(responseData.warning);
//         } else {
//           toast.success(responseData.message || 'Room updated successfully.');
//         }
//         modalClose();
//         await fetchRoomDetails();
//       } else {
//         toast.error('Failed to update room: ' + (responseData.detail || 'Unknown error'));
//       }
//     } catch (error) {
//       console.error('Error updating room:', error);
//       toast.error('Error updating room: ' + error.message);
//     }
//   };

//   const handleDeleteRoom = async (roomnumber) => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/rooms/${roomnumber}`, {
//         method: 'DELETE',
//         headers: { Accept: 'application/json' },
//       });

//       if (response.ok) {
//         toast.success('Room deleted successfully');
//         setIsPopOpen(false);
//         setTimeout(() => window.location.reload(), 1000);
//       } else {
//         const result = await response.json();
//         toast.error(result.detail || 'Failed to delete room');
//       }
//     } catch (error) {
//       console.error('Error deleting room:', error);
//       toast.error('Error deleting room');
//     }
//   };

//   // ============================================
//   // Fetch rooms
//   // ============================================
//   const fetchRoomDetails = async () => {
//     try {
//       const response = await fetch(ROOMS_API_URL, {
//         headers: { accept: '*/*' },
//       });
//       const data = await response.json();

//       const list = Array.isArray(data) ? data : data?.rooms || [];

//       const normalized = list.map((r) => {
//         const amenityObjects = (r.amenities || []).map((a) => {
//           if (typeof a === 'object' && a !== null) {
//             return {
//               factorid: a.factorid,
//               name: a.name,
//               value: a.value,
//               value_type: a.value_type,
//               contribution: a.contribution,
//             };
//           }
//           const found = allFactors.find((f) => String(f.factorid) === String(a));
//           return {
//             factorid: Number(a),
//             name: found ? found.factorname : String(a),
//             value: found ? (found.default_value ?? found.defaultpercentage ?? 0) : 0,
//             value_type: found ? found.value_type || 'flat' : 'flat',
//             contribution: 0,
//           };
//         });

//         const amenityIds = amenityObjects.map((a) => String(a.factorid));

//         return {
//           roomid: r.roomid,
//           roomnumber: r.roomnumber,
//           roomtypeid: r.roomtypeid,
//           status: r.status,
//           auto_assign_room: r.auto_assign_room || false,
//           amenities: amenityObjects,
//           amenityIds,
//           condition: normalizeCondition(r.condition),
//           condition_notes: r.condition_notes || '',
//           roomtype: r.roomtype?.roomtypename || '',
//           room_type: r.roomtype?.roomtypename || '',
//           baseprice: r.roomtype?.baseprice ?? 0,
//           base_price: r.base_price ?? r.roomtype?.baseprice ?? 0,
//           amenities_total: r.amenities_total ?? 0,
//           flat_total: r.flat_total ?? 0,
//           percentage_total: r.percentage_total ?? 0,
//           final_price: r.final_price ?? r.roomtype?.baseprice ?? 0,
//         };
//       });

//       setRooms(normalized);

//       // Group: roomType → status (lowercase) → [rooms]
//       const grouped = {};
//       normalized.forEach((room) => {
//         const rtype = room.roomtype || 'Unknown';
//         const status = String(room.status || '').toLowerCase();

//         if (!grouped[rtype]) grouped[rtype] = {};
//         if (!grouped[rtype][status]) grouped[rtype][status] = [];
//         grouped[rtype][status].push(room);
//       });

//       setRoomData(grouped);
//     } catch (error) {
//       console.error('Error fetching room details:', error);
//       toast.error('Could not load rooms');
//     }
//   };

//   useEffect(() => {
//     fetchRoomDetails();

//     const fetchAvailableRoomTypes = async () => {
//       try {
//         const response = await fetch(AVAILABILITY_API_URL);
//         const data = await response.json();
//         setAvailableRoomTypes(data.available_room_types);
//       } catch (error) {
//         console.error('Error fetching available room types:', error);
//         setAvailableRoomTypes([]);
//       }
//     };

//     fetchAvailableRoomTypes();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   // ============================================
//   // Filter
//   // ============================================
//   const filteredRooms = Object.keys(roomData).map((roomType) => {
//     const roomsInType = Object.keys(roomData[roomType]).flatMap((status) =>
//       roomData[roomType][status].filter((room) => {
//         const matchesSearch =
//           (room?.baseprice?.toString()?.includes(searchTerm) ?? false) ||
//           (room?.roomnumber?.toString()?.includes(searchTerm) ?? false);

//         const matchesStatus =
//           roomStatus === 'all' || String(room.status).toLowerCase() === roomStatus;

//         const matchesRoomType =
//           selectedRoomType === 'all' ||
//           String(room.room_type).toLowerCase() === selectedRoomType.toLowerCase();

//         const matchesAvailability =
//           !onlyAvailableRooms || String(room.status).toLowerCase() === 'available';

//         const matchesCondition = conditionFilter === 'all' || room.condition === conditionFilter;

//         return (
//           matchesSearch &&
//           matchesStatus &&
//           matchesRoomType &&
//           matchesAvailability &&
//           matchesCondition
//         );
//       })
//     );

//     const availableCount = roomsInType.filter(
//       (r) => String(r.status).toLowerCase() === 'available'
//     ).length;
//     const occupiedCount = roomsInType.filter(
//       (r) => String(r.status).toLowerCase() === 'occupied'
//     ).length;
//     const reservedCount = roomsInType.filter(
//       (r) => String(r.status).toLowerCase() === 'reserved'
//     ).length;
//     const maintenanceCount = roomsInType.filter(
//       (r) => String(r.status).toLowerCase() === 'under maintenance'
//     ).length;

//     return {
//       roomType,
//       availableCount,
//       occupiedCount,
//       reservedCount,
//       maintenanceCount,
//       rooms: roomsInType,
//     };
//   });

//   const handleClearFilters = () => {
//     setSearchTerm('');
//     setRoomStatus('all');
//     setSelectedRoomType('all');
//     setOnlyAvailableRooms(false);
//     setConditionFilter('all');
//   };

//   const handleOpenModal = () => {
//     setNewRoom({ ...DEFAULT_NEW_ROOM });
//     setIsModalOpen(true);
//   };
//   const handleCloseModal = () => {
//     setIsModalOpen(false);
//     setNewRoom({ ...DEFAULT_NEW_ROOM });
//   };

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setNewRoom((prev) => ({ ...prev, [name]: value }));
//   };

//   // Toggle amenity in NEW room modal
//   const toggleNewRoomAmenity = (code) => {
//     setNewRoom((prev) => {
//       const current = prev.amenities || [];
//       const updated = current.includes(code)
//         ? current.filter((a) => a !== code)
//         : [...current, code];
//       return { ...prev, amenities: updated };
//     });
//   };

//   const openModal = (room) => {
//     setSelectedRoom({
//       ...room,
//       amenities: room.amenityIds || [],
//       condition: room.condition || 'all_working',
//       condition_notes: room.condition_notes || '',
//       auto_assign_room: room.auto_assign_room || false,
//     });
//     setIsPopOpen(true);
//   };

//   const modalClose = () => {
//     setIsPopOpen(false);
//     setSelectedRoom(null);
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setSelectedRoom((prev) => ({ ...prev, [name]: value }));
//   };

//   const toggleAmenity = (code) => {
//     setSelectedRoom((prev) => {
//       const current = prev.amenities || [];
//       const updated = current.includes(code)
//         ? current.filter((a) => a !== code)
//         : [...current, code];
//       return { ...prev, amenities: updated };
//     });
//   };

//   return (
//     <div className="p-6 bg-gray-100 ">
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">Room Inventory</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() => navigate('/dashboard/mdm/room-management/overview')}
//             className="btn"
//           >
//             Overview
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/room-management/rooms')}
//             className="btn text-black font-bold"
//           >
//             Room Management
//           </button>
//         </div>
//       </header>

//       <h1 className="text-2xl font-bold text-center text-black">Room Management</h1>
//       <h1 className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
//         Efficient room management ensures seamless booking, maintenance, and comfort, providing
//         guests with an exceptional experience throughout their stay.
//       </h1>
//       <hr className="border border-gray-400 w-1/2 mx-auto mb-7" />

//       {/* Filters */}
//       <div className="flex justify-between items-center gap-2 mb-8 text-sm w-full flex-wrap">
//         <div className="w-full mb-4 sm:mb-0 relative flex-1 min-w-[180px]">
//           <input
//             type="text"
//             className="w-full px-2 py-2 pl-10 border border-gray-300 rounded-md"
//             placeholder="Find a room"
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//           />
//           <FiSearch
//             size={16}
//             className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600"
//           />
//         </div>

//         <div className="w-full mb-4 sm:mb-0 flex-1 min-w-[150px]">
//           <select
//             className="w-full px-4 py-2 border border-gray-300 rounded-lg"
//             value={roomStatus}
//             onChange={(e) => setRoomStatus(e.target.value)}
//           >
//             <option value="all">All Status</option>
//             <option value="available">Available</option>
//             <option value="reserved">Reserved</option>
//             <option value="occupied">Occupied</option>
//             <option value="under maintenance">Under Maintenance</option>
//           </select>
//         </div>

//         <div className="w-full mb-4 sm:mb-0 flex-1 min-w-[150px]">
//           <select
//             className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none"
//             value={selectedRoomType}
//             onChange={(e) => setSelectedRoomType(e.target.value)}
//           >
//             <option value="all">All Room Types</option>
//             {Object.keys(roomData).map((rt) => (
//               <option key={rt} value={rt}>
//                 {rt}
//               </option>
//             ))}
//           </select>
//         </div>

//         <div className="w-full mb-4 sm:mb-0 flex-1 min-w-[150px]">
//           <select
//             className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none"
//             value={conditionFilter}
//             onChange={(e) => setConditionFilter(e.target.value)}
//           >
//             <option value="all">All Conditions</option>
//             {CONDITIONS.map((c) => (
//               <option key={c.code} value={c.code}>
//                 {c.label}
//               </option>
//             ))}
//           </select>
//         </div>

//         <div>
//           <button
//             onClick={handleOpenModal}
//             className="px-6 py-2 bg-black text-white rounded-lg whitespace-nowrap"
//           >
//             Add Room
//           </button>
//         </div>

//         <Link to={'/dashboard/mdm/room-management/rooms/addroomtype'}>
//           <div>
//             <button className="px-6 py-2 bg-black text-white rounded-lg whitespace-nowrap">
//               Add Room Type
//             </button>
//           </div>
//         </Link>

//         <div>
//           <button
//             onClick={handleClearFilters}
//             className="px-6 py-2 bg-red-500 text-white rounded-lg whitespace-nowrap"
//           >
//             Clear All
//           </button>
//         </div>
//       </div>

//       {/* Summary Cards */}
//       <div className="w-full mb-4">
//         {filteredRooms.length > 0 ? (
//           filteredRooms
//             .filter(({ roomType }) => selectedRoomType === roomType)
//             .map(
//               ({
//                 roomType,
//                 availableCount,
//                 occupiedCount,
//                 reservedCount,
//                 maintenanceCount,
//                 rooms,
//               }) => (
//                 <div key={roomType} className="mb-6">
//                   <h2 className="text-lg font-bold mb-2 text-gray-800">{roomType}</h2>
//                   <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
//                     <div className="bg-white p-5 rounded-lg shadow-md">
//                       <h3 className="font-semibold text-lg text-gray-700">Total Rooms</h3>
//                       <p className="text-3xl font-bold text-gray-800">{rooms.length}</p>
//                     </div>
//                     <div className="bg-white p-5 rounded-lg shadow-md">
//                       <h3 className="font-semibold text-lg text-gray-700">Available Rooms</h3>
//                       <p className="text-3xl font-bold text-gray-800">{availableCount}</p>
//                     </div>
//                     <div className="bg-white p-5 rounded-lg shadow-md">
//                       <h3 className="font-semibold text-lg text-gray-700">Reserved Rooms</h3>
//                       <p className="text-3xl font-bold text-gray-800">{reservedCount}</p>
//                     </div>
//                     <div className="bg-white p-5 rounded-lg shadow-md">
//                       <h3 className="font-semibold text-lg text-gray-700">Occupied Rooms</h3>
//                       <p className="text-3xl font-bold text-gray-800">{occupiedCount}</p>
//                     </div>
//                     <div className="bg-white p-5 rounded-lg shadow-md">
//                       <h3 className="font-semibold text-lg text-gray-700">Under Maintenance</h3>
//                       <p className="text-3xl font-bold text-gray-800">{maintenanceCount}</p>
//                     </div>
//                   </div>
//                 </div>
//               )
//             )
//         ) : (
//           <div className="text-center text-gray-700 mt-4 p-4">
//             <p className="text-lg font-semibold text-gray-800">Loading...</p>
//           </div>
//         )}
//       </div>

//       {/* Room Tables */}
//       <div className="space-y-8 text-sm">
//         {filteredRooms.map(({ roomType, rooms }) => {
//           if (rooms.length === 0) return null;

//           return (
//             <div key={roomType}>
//               <h2 className="bg-white text-black font-bold text-left p-3 text-base rounded-t-lg shadow-md">
//                 {roomType}
//               </h2>

//               <div className="overflow-x-auto rounded-b-lg">
//                 <table className="min-w-full table-auto bg-white">
//                   <thead className="bg-gray-900 text-white">
//                     <tr>
//                       <th className="py-3 px-4 text-left font-medium">Room No.</th>
//                       <th className="py-3 px-4 text-left font-medium">Status</th>
//                       <th className="py-3 px-4 text-left font-medium">Base Price</th>
//                       <th className="py-3 px-4 text-left font-medium">Amenities</th>
//                       <th className="py-3 px-4 text-left font-medium">Amenities Total</th>
//                       <th className="py-3 px-4 text-left font-medium">Final Price</th>
//                       <th className="py-3 px-4 text-left font-medium">Condition</th>
//                       <th className="py-3 px-4 text-left font-medium">Auto-Assign</th>
//                     </tr>
//                   </thead>
//                   <tbody className="text-gray-700 border border-gray-200">
//                     {rooms.map((room, index) => {
//                       const amenities = room.amenities || [];
//                       const condition = room.condition || 'all_working';

//                       return (
//                         <tr
//                           key={room.roomid}
//                           className={`${
//                             index % 2 === 0 ? 'bg-white' : 'bg-gray-100'
//                           } hover:bg-gray-200 cursor-pointer align-top`}
//                           onClick={() => openModal(room)}
//                         >
//                           <td className="py-3 px-4 font-semibold">{room.roomnumber}</td>
//                           <td className="py-3 px-4">{room.status}</td>
//                           <td className="py-3 px-4">₹{room.base_price}</td>

//                           <td className="py-3 px-4">
//                             {amenities.length > 0 ? (
//                               <div className="flex flex-col gap-1">
//                                 {amenities.map((a, i) => (
//                                   <div
//                                     key={`${a.factorid}-${i}`}
//                                     className="flex items-center gap-2 text-xs"
//                                   >
//                                     <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
//                                       {a.name}
//                                     </span>
//                                   </div>
//                                 ))}
//                               </div>
//                             ) : (
//                               <span className="text-xs text-gray-400">None</span>
//                             )}
//                           </td>

//                           <td className="py-3 px-4 font-medium">
//                             {room.amenities_total > 0 ? (
//                               <span className="text-green-700">+₹{room.amenities_total}</span>
//                             ) : (
//                               <span className="text-gray-400">₹0</span>
//                             )}
//                           </td>

//                           <td className="py-3 px-4 font-bold text-black">₹{room.final_price}</td>

//                           <td className="py-3 px-4">
//                             <span className={`font-semibold ${getConditionColor(condition)}`}>
//                               {getConditionLabel(condition)}
//                             </span>
//                             {condition !== 'all_working' && room.condition_notes && (
//                               <p className="text-xs text-gray-500 mt-0.5">{room.condition_notes}</p>
//                             )}
//                           </td>

//                           <td className="py-3 px-4">
//                             <label className="relative inline-flex items-center cursor-default pointer-events-none">
//                               <input
//                                 type="checkbox"
//                                 className="sr-only peer"
//                                 checked={room.auto_assign_room}
//                                 readOnly
//                                 disabled
//                               />
//                               <div className="w-10 h-6 bg-gray-300 rounded-full peer peer-checked:bg-black transition-all"></div>
//                               <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-full"></div>
//                             </label>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//               </div>
//             </div>
//           );
//         })}
//       </div>

//       {/* ============================================ */}
//       {/* Add Room Modal */}
//       {/* ============================================ */}
//       {isModalOpen && (
//         <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center p-4">
//           <div className="relative bg-white p-6 rounded-md shadow-lg w-full max-w-2xl text-xs max-h-[90vh] overflow-y-auto">
//             <button
//               onClick={handleCloseModal}
//               className="absolute top-4 right-3 text-red-500 hover:text-red-600"
//             >
//               <IoClose size={28} />
//             </button>

//             <h2 className="text-xl font-bold mb-4">Add New Room</h2>

//             <div className="space-y-3">
//               {/* Room Number + Room Type */}
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
//                 <div>
//                   <label className="block text-sm font-semibold mb-1">Room Number *</label>
//                   <input
//                     type="number"
//                     name="room_no"
//                     placeholder="e.g. 501"
//                     value={newRoom.room_no}
//                     onChange={handleInputChange}
//                     className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-semibold mb-1">Room Type *</label>
//                   <select
//                     name="room_type"
//                     value={newRoom.room_type}
//                     onChange={handleInputChange}
//                     className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
//                   >
//                     <option value="" disabled>
//                       Select Room Type
//                     </option>
//                     {roomTypes.length === 0 ? (
//                       <option disabled value="">
//                         Loading room types...
//                       </option>
//                     ) : (
//                       roomTypes.map((type) => (
//                         <option key={type.roomtypeid} value={type.roomtypename}>
//                           {type.roomtypename}
//                         </option>
//                       ))
//                     )}
//                   </select>
//                 </div>
//               </div>

//               {/* Status + Auto-Assign */}
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
//                 <div>
//                   <label className="block text-sm font-semibold mb-1">Status *</label>
//                   <select
//                     name="status"
//                     value={newRoom.status}
//                     onChange={handleInputChange}
//                     className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
//                   >
//                     <option value="Available">Available</option>
//                     <option value="Reserved">Reserved</option>
//                     <option value="Occupied">Occupied</option>
//                     <option value="Under Maintenance">Under Maintenance</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-sm font-semibold mb-1">Auto-Assign Room</label>
//                   <button
//                     type="button"
//                     onClick={() =>
//                       setNewRoom((prev) => ({
//                         ...prev,
//                         auto_assign_room: !prev.auto_assign_room,
//                       }))
//                     }
//                     className={`mt-1 w-11 h-6 rounded-full transition ${
//                       newRoom.auto_assign_room ? 'bg-black' : 'bg-gray-300'
//                     }`}
//                   >
//                     <span
//                       className={`block w-4 h-4 bg-white rounded-full transition-transform  ${
//                         newRoom.auto_assign_room ? 'translate-x-6' : 'translate-x-1'
//                       }`}
//                     />
//                   </button>
//                   <span className="ml-3 text-xs text-gray-500">
//                     {newRoom.auto_assign_room ? 'Enabled' : 'Disabled'}
//                   </span>
//                 </div>
//               </div>

//               {/* Amenities */}
//               <div className="border border-gray-200 rounded-md p-3">
//                 <label className="block text-sm font-semibold mb-1">Room Attributes</label>
//                 <p className="text-xs text-gray-500 mb-2">
//                   Tick the attributes this room has. Prices will auto-apply to the room's final
//                   rate.
//                 </p>

//                 {factorsLoading ? (
//                   <p className="text-xs text-gray-400 italic">Loading attributes...</p>
//                 ) : roomAmenities.length === 0 ? (
//                   <p className="text-xs text-gray-500 italic">
//                     No room attributes configured. Create factors marked as "Room Attribute" in
//                     Factor Master first.
//                   </p>
//                 ) : (
//                   <div className="grid grid-cols-2 gap-2">
//                     {roomAmenities.map((amenity) => {
//                       const isChecked = (newRoom.amenities || []).includes(amenity.code);
//                       return (
//                         <label
//                           key={amenity.code}
//                           className={`flex items-center gap-2 px-3 py-2 rounded-md border-2 cursor-pointer transition ${
//                             isChecked
//                               ? 'bg-blue-50 border-blue-400 text-blue-800 font-semibold'
//                               : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
//                           }`}
//                         >
//                           <input
//                             type="checkbox"
//                             checked={isChecked}
//                             onChange={() => toggleNewRoomAmenity(amenity.code)}
//                             className="h-4 w-4"
//                           />
//                           <span className="text-xs flex-1">{amenity.label}</span>
//                           <span className="text-[10px] text-gray-500">
//                             {amenity.value_type === 'percentage'
//                               ? `${amenity.value}%`
//                               : `₹${amenity.value}`}
//                           </span>
//                         </label>
//                       );
//                     })}
//                   </div>
//                 )}
//               </div>

//               {/* Condition + Notes */}
//               <div className="border border-gray-200 rounded-md p-3">
//                 <label className="block text-sm font-semibold mb-1">Condition</label>
//                 <select
//                   name="condition"
//                   value={newRoom.condition}
//                   onChange={handleInputChange}
//                   className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm"
//                 >
//                   {CONDITIONS.map((c) => (
//                     <option key={c.code} value={c.code}>
//                       {c.label}
//                     </option>
//                   ))}
//                 </select>

//                 {newRoom.condition && newRoom.condition !== 'all_working' && (
//                   <div className="mt-3">
//                     <label className="block text-xs font-medium mb-1">
//                       Condition Notes (Optional)
//                     </label>
//                     <input
//                       type="text"
//                       name="condition_notes"
//                       value={newRoom.condition_notes || ''}
//                       onChange={handleInputChange}
//                       placeholder="e.g., Compressor failure, technician arriving tomorrow"
//                       className="w-full px-3 py-2 border rounded-md text-xs"
//                     />
//                   </div>
//                 )}
//               </div>
//             </div>

//             <div className="flex justify-end space-x-3 text-sm mt-5">
//               <button
//                 onClick={handleCloseModal}
//                 className="px-5 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleAddRoom}
//                 className="px-5 py-2 bg-black text-white rounded-md hover:bg-gray-800"
//               >
//                 Add Room
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Room Detail / Edit Modal */}
//       {isPopOpen && selectedRoom && (
//         <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50 p-4">
//           <div className="relative bg-white p-8 rounded-lg shadow-lg w-full max-w-2xl text-sm text-black max-h-[90vh] overflow-y-auto">
//             <button
//               onClick={modalClose}
//               className="absolute top-4 right-3 text-red-500 hover:text-red-600"
//             >
//               <IoClose size={28} />
//             </button>
//             <h3 className="text-xl font-bold text-center">Room Details</h3>
//             <p className="text-sm text-gray-500 mb-4 text-center font-semibold">
//               Update the room details below
//             </p>
//             <div className="space-y-4 text-xs">
//               <div>
//                 <label className="block text-black">Room Number</label>
//                 <input
//                   type="text"
//                   value={selectedRoom.roomnumber}
//                   disabled
//                   className="w-full px-4 py-2 border rounded-md cursor-not-allowed"
//                 />
//               </div>

//               <div>
//                 <label className="block text-black">Room Type</label>
//                 <input
//                   type="text"
//                   value={selectedRoom.room_type}
//                   disabled
//                   className="w-full px-4 py-2 border rounded-md cursor-not-allowed"
//                 />
//               </div>

//               <div>
//                 <label className="block text-black">Status</label>
//                 <select
//                   name="status"
//                   value={selectedRoom.status}
//                   onChange={handleChange}
//                   className="w-full px-4 py-2 border rounded-md"
//                 >
//                   <option value="Available">Available</option>
//                   <option value="Reserved">Reserved</option>
//                   <option value="Occupied">Occupied</option>
//                   <option value="Under Maintenance">Under Maintenance</option>
//                 </select>
//               </div>

//               <div>
//                 <label className="block text-black">Base Price</label>
//                 <input
//                   type="number"
//                   value={selectedRoom.base_price}
//                   disabled
//                   className="w-full px-4 py-2 border rounded-md cursor-not-allowed"
//                 />
//               </div>

//               {/* Amenities */}
//               <div className="mt-4 pt-4 border-t border-gray-200">
//                 <label className="block text-black text-sm font-semibold mb-2">
//                   Room Amenities
//                 </label>
//                 <p className="text-xs text-gray-500 mb-3">
//                   Tick which amenities this room actually has. Pricing updates automatically.
//                 </p>

//                 {factorsLoading ? (
//                   <p className="text-xs text-gray-400 italic">Loading amenities...</p>
//                 ) : roomAmenities.length === 0 ? (
//                   <p className="text-xs text-gray-500 italic">
//                     No room amenities configured. Create factors marked as "Room Amenity" in Factor
//                     Master.
//                   </p>
//                 ) : (
//                   <div className="grid grid-cols-2 gap-2">
//                     {roomAmenities.map((amenity) => {
//                       const isChecked = (selectedRoom.amenities || []).includes(amenity.code);
//                       return (
//                         <label
//                           key={amenity.code}
//                           className={`flex items-center gap-2 px-3 py-2 rounded-md border-2 cursor-pointer transition ${
//                             isChecked
//                               ? 'bg-blue-50 border-blue-400 text-blue-800 font-semibold'
//                               : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
//                           }`}
//                         >
//                           <input
//                             type="checkbox"
//                             checked={isChecked}
//                             onChange={() => toggleAmenity(amenity.code)}
//                             className="h-4 w-4"
//                           />
//                           <span className="text-xs flex-1">{amenity.label}</span>
//                           <span className="text-[10px] text-gray-500">
//                             {amenity.value_type === 'percentage'
//                               ? `${amenity.value}%`
//                               : `₹${amenity.value}`}
//                           </span>
//                         </label>
//                       );
//                     })}
//                   </div>
//                 )}
//               </div>

//               {/* Condition */}
//               <div className="mt-4 pt-4 border-t border-gray-200">
//                 <label className="block text-black text-sm font-semibold mb-2">
//                   Current Condition
//                 </label>
//                 <p className="text-xs text-gray-500 mb-3">
//                   Mark any issue with this room. This affects how it's handled during bookings.
//                 </p>

//                 <select
//                   name="condition"
//                   value={selectedRoom.condition || 'all_working'}
//                   onChange={handleChange}
//                   className="w-full px-4 py-2 border rounded-md"
//                 >
//                   {CONDITIONS.map((c) => (
//                     <option key={c.code} value={c.code}>
//                       {c.label}
//                     </option>
//                   ))}
//                 </select>

//                 {selectedRoom.condition && selectedRoom.condition !== 'all_working' && (
//                   <div className="mt-3">
//                     <label className="block text-black text-xs font-medium mb-1">
//                       Condition Notes (Optional)
//                     </label>
//                     <input
//                       type="text"
//                       name="condition_notes"
//                       value={selectedRoom.condition_notes || ''}
//                       onChange={handleChange}
//                       placeholder="e.g., Compressor failure, technician arriving tomorrow"
//                       className="w-full px-3 py-2 border rounded-md text-xs"
//                     />
//                   </div>
//                 )}
//               </div>

//               {/* Auto-Assign */}
//               <div className="mt-4 pt-4 border-t border-gray-200">
//                 <div className="flex items-center justify-between mb-2">
//                   <div>
//                     <label className="block text-black text-sm font-semibold">
//                       Auto-Assign Room
//                     </label>
//                     <p className="text-xs text-gray-500 mt-1">
//                       When enabled, this room is prioritized for automatic guest assignment.
//                     </p>
//                   </div>
//                   <label className="relative inline-flex items-center cursor-pointer ml-4">
//                     <input
//                       type="checkbox"
//                       checked={selectedRoom.auto_assign_room}
//                       onChange={(e) =>
//                         setSelectedRoom((prev) => ({
//                           ...prev,
//                           auto_assign_room: e.target.checked,
//                         }))
//                       }
//                       className="sr-only peer"
//                     />
//                     <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-black transition-all"></div>
//                     <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-full"></div>
//                   </label>
//                 </div>
//               </div>
//             </div>

//             <div className="mt-6 flex justify-end gap-2">
//               <button onClick={handleUpdate} className="bg-black text-white px-4 py-2 rounded-md">
//                 Save Changes
//               </button>

//               <button
//                 onClick={() => handleDeleteRoom(selectedRoom.roomnumber)}
//                 className="px-5 py-2 bg-red-500 text-white rounded-md"
//               >
//                 Delete Room
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <ToastContainer position="top-right" autoClose={3000} hideProgressBar className="mt-12" />
//     </div>
//   );
// };

// export default RoomManagement;
